import { useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Sparkles, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MarkdownField } from "@/components/MarkdownField";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, errText } from "@/lib/api";

const GENERAL = "__general__";
const NONE = "__none__";

export const BriefDraftReview = ({ note, matters, onSaved, onDiscarded }) => {
  const [items, setItems] = useState(
    (note.items || []).map((i) => ({ ...i, include: i.include !== false })),
  );
  const [general, setGeneral] = useState(note.general_notes || "");
  const [showTranscript, setShowTranscript] = useState(false);
  const [saving, setSaving] = useState(false);

  const update = (idx, patch) =>
    setItems((list) => list.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  const pickTarget = (idx, value) => {
    if (value === GENERAL) return update(idx, { is_general: true, matter_id: null, event_id: null });
    if (value === NONE) return update(idx, { is_general: false, matter_id: null, event_id: null });
    const matter = matters.find((m) => m.matter_id === value);
    update(idx, {
      is_general: false,
      matter_id: value,
      matter_title: matter?.title || null,
      event_id: items[idx]?.matter_id === value ? items[idx].event_id : null,
    });
  };

  const save = async () => {
    const included = items.filter((i) => i.include && i.instruction?.trim());
    if (!included.length && !general.trim()) {
      return toast.error("Keep at least one instruction, or discard the note");
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/voice/notes/${note.note_id}/confirm`, {
        items: included.map((i) => ({
          include: true,
          matter_id: i.is_general ? null : i.matter_id || null,
          event_id: i.is_general ? null : i.event_id || null,
          instruction: i.instruction.trim(),
          bare_acts: i.bare_acts?.trim() || null,
          judgments: i.judgments?.trim() || null,
          is_general: !!i.is_general,
        })),
        general_notes: general.trim() || null,
      });
      toast.success(`${data.saved} instruction(s) saved to the brief`);
      onSaved();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setSaving(false);
    }
  };

  const discard = async () => {
    try {
      await api.delete(`/voice/notes/${note.note_id}`);
      toast.success("Draft discarded");
      onDiscarded();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  return (
    <section className="surface p-6" data-testid="brief-draft-review">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="label-caps inline-flex items-center gap-2">
            <Sparkles className="h-4 w-4" /> Review before it goes in
          </p>
          <h2 className="text-base md:text-lg font-heading font-semibold mt-2">
            {items.length} instruction(s) read from your note
          </h2>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            data-testid="draft-save-btn"
            onClick={save}
            disabled={saving}
            className="rounded-full h-12 px-6"
          >
            {saving ? "Saving…" : "Save to brief"}
          </Button>
          <Button
            variant="outline"
            data-testid="draft-discard-btn"
            onClick={discard}
            className="rounded-full h-12 px-5 text-destructive border-destructive/40 hover:bg-destructive/10"
          >
            <Trash2 className="h-4 w-4 mr-2" /> Discard
          </Button>
        </div>
      </div>

      <button
        data-testid="draft-transcript-toggle"
        onClick={() => setShowTranscript((v) => !v)}
        className="mt-5 inline-flex items-center gap-2 text-sm font-medium hover:underline"
      >
        {showTranscript ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        {showTranscript ? "Hide" : "Show"} what was heard
      </button>
      {showTranscript ? (
        <p
          data-testid="draft-transcript"
          className="mt-3 text-sm leading-relaxed text-muted-foreground border border-border rounded-sm p-4 whitespace-pre-line"
        >
          {note.transcript}
        </p>
      ) : null}

      <div className="mt-6 space-y-4">
        {items.map((item, idx) => (
          <div
            key={idx}
            data-testid={`draft-item-${idx}`}
            className="border border-border rounded-sm p-4 space-y-3"
          >
            <div className="flex flex-wrap items-center gap-4 justify-between">
              <label className="flex items-center gap-2.5">
                <Checkbox
                  data-testid={`draft-include-${idx}`}
                  checked={item.include}
                  onCheckedChange={(v) => update(idx, { include: !!v })}
                />
                <span className="text-sm font-medium">Include</span>
              </label>
              {item.event_label ? (
                <span className="chip chip-session">{item.event_label}</span>
              ) : item.heard_as ? (
                <span className="chip chip-oos">heard as “{item.heard_as}”</span>
              ) : (
                <span className="chip chip-fixed">no listing matched</span>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label className="label-caps">Matter</Label>
                <Select
                  value={item.is_general ? GENERAL : item.matter_id || NONE}
                  onValueChange={(v) => pickTarget(idx, v)}
                >
                  <SelectTrigger
                    data-testid={`draft-matter-${idx}`}
                    className="rounded-sm h-11 text-base"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value={GENERAL}>General — whole day</SelectItem>
                    <SelectItem value={NONE}>Unassigned</SelectItem>
                    {matters.map((m) => (
                      <SelectItem key={m.matter_id} value={m.matter_id}>
                        {m.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="label-caps">Bare Acts required</Label>
                <Input
                  data-testid={`draft-bare-acts-${idx}`}
                  value={item.bare_acts || ""}
                  onChange={(e) => update(idx, { bare_acts: e.target.value })}
                  className="rounded-sm h-11 text-base"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-caps">Judgments required</Label>
                <Input
                  data-testid={`draft-judgments-${idx}`}
                  value={item.judgments || ""}
                  onChange={(e) => update(idx, { judgments: e.target.value })}
                  className="rounded-sm h-11 text-base"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="label-caps">Instruction</Label>
              <MarkdownField
                value={item.instruction}
                onChange={(v) => update(idx, { instruction: v })}
                testId={`draft-instruction-${idx}`}
                minHeight={80}
              />
            </div>
          </div>
        ))}

        <div className="space-y-2">
          <Label className="label-caps">General instructions for the day</Label>
          <MarkdownField
            value={general}
            onChange={setGeneral}
            testId="draft-general-notes"
            minHeight={80}
            placeholder="e.g. Reach court by 9:45 and collect the files from the clerk"
          />
        </div>
      </div>
    </section>
  );
};
